import { everyInIterable, filterIterable, takeFromIterable } from '../iterable';
import { NBSP, textToDocumentFragment } from '../textToHtml';
import { isOnlyCollapsibleWhitespace } from '../whitespace';
import { EDITOR_DATA_LIST_TYPE, isBlockLevelContent, isPhrasingContent } from './content';
import { getElementContents } from './element';
import { isListElement, isListItemElement, ListElement } from './list';
import {
  findPreviousNonWhitespaceSibling,
  isComment,
  isElement,
  isElementWithTagName,
  isText,
  isWhitespaceText,
  removeAllChildren,
  replaceAllChildren,
  unwrapNode,
} from './node';
import { isBreakElement, isParagraphElement } from './paragraph';
import { isTableElement } from './table';

/**
 * Unwraps phrasing content from superfluous containers, typically originating from the clipboard.
 *
 * Examples: a single table with a single table cell, a single list with a single list item.
 */
export function unwrapPhrasingContentContainers(
  container: Node & ParentNode,
  shouldKeepPreformattedText = false,
) {
  const document = container.ownerDocument;
  if (!document) {
    return;
  }

  // Filter out any non-significant child nodes.
  const childNodes = Array.from(container.childNodes).filter(
    childNode => !isWhitespaceText(childNode) && !isBreakElement(childNode),
  );

  // Remove an empty next sibling of the same type, which is typically the result of the user triple-cliking
  // to select an element and extending the selection to the start of the next element.
  if (
    childNodes.length === 2 &&
    isElement(childNodes[0]) &&
    isBlockLevelContent(childNodes[0]) &&
    childNodes[0].nodeName === childNodes[1].nodeName &&
    childNodes[1].childNodes.length === 0
  ) {
    childNodes.pop();
  }

  // Check whether we now have a single child
  if (childNodes.length !== 1) {
    return;
  }

  const childNode = childNodes[0];

  // If it is a table, find a single table cell and grab its contents.
  if (isTableElement(childNode)) {
    const tableCells = container.querySelectorAll('td, th');
    if (!(tableCells.length === 1 || (tableCells.length === 2 && tableCells[1].childNodes.length === 0))) {
      return;
    }

    const tableCell = tableCells[0];
    removeAllChildren(container);
    container.appendChild(getElementContents(tableCell));
  }
  // If it is a list, find a single list item and grab its contents, permitting it to be followed by a second list item
  // that is empty in case the user triple-clicked to select.
  else if (isListElement(childNode)) {
    const listItems = container.querySelectorAll('li');
    if (!(listItems.length === 1 || (listItems.length === 2 && listItems[1].childNodes.length === 0))) {
      return;
    }

    const listItem = listItems[0];
    removeAllChildren(container);
    container.appendChild(getElementContents(listItem));
  }
  // If it is pre-formatted text with more than one line, and we want to keep pre-formatted text intact, do nothing.
  else if (
    isElementWithTagName(childNode, 'pre') &&
    /[\r\n]/.test(childNode.textContent || '') &&
    shouldKeepPreformattedText
  ) {
    // Do nothing.
  }
  // If it is pre-formatted text, perform text to HTML conversion and apply monospace font.
  else if (
    isElementWithTagName(childNode, 'pre') &&
    childNode.childNodes.length > 0 &&
    everyInIterable(childNode.childNodes, isText)
  ) {
    const text = childNode.textContent || '';
    const fragment = textToDocumentFragment(document, text, { shouldAddMonospaceStyle: true });
    removeAllChildren(container);
    container.appendChild(fragment);
  }
  // If it is some other non-phrasing content element containing only phrasing content, grab its contents.
  else if (
    isElement(childNode) &&
    isBlockLevelContent(childNode) &&
    childNode.childNodes.length > 0 &&
    everyInIterable(childNode.childNodes, isPhrasingContent)
  ) {
    removeAllChildren(container);
    container.appendChild(getElementContents(childNode));
  }
}

export function wrapContentForParent(newParentNode: Node | null, container: Node) {
  const document = container.ownerDocument;
  if (!document) {
    return;
  }

  const childNodes = Array.from(container.childNodes);
  const isBlockContext = childNodes.some(isBlockLevelContent);

  let currentWrapperNode: Node | null = null;
  childNodes.forEach(childNode => {
    // If there are any blocks in the container, skip interspersed whitespace.
    if (isBlockContext && isWhitespaceText(childNode)) {
      container.removeChild(childNode);
      return;
    }

    // If this child is legal in the new parent, stop appending to the wrapper (if any) and keep the child.
    if (isLegalParent(newParentNode, childNode)) {
      currentWrapperNode = null;
      return;
    }

    // If this child is legal in the current wrapper, append it.
    if (currentWrapperNode && isLegalParent(currentWrapperNode, childNode)) {
      currentWrapperNode.appendChild(childNode);
      return;
    }

    // This child is not legal in the new parent or in the current wrapper, so create a new wrapper.
    currentWrapperNode = createWrapperForIllegalParent(document, newParentNode, childNode);
    container.insertBefore(currentWrapperNode, childNode);
    currentWrapperNode.appendChild(childNode);
  });
}

/**
 * Determines if the given child is legal in the given parent.
 *
 * Use `null` as the `parentNode` to indicate the root of the document (editor container).
 */
function isLegalParent(parentNode: Node | null, childNode: Node): boolean {
  if (isComment(childNode)) {
    return true;
  }

  if (isListItemElement(childNode)) {
    return isListElement(parentNode);
  }

  if (isListElement(parentNode)) {
    return isListItemElement(childNode);
  }

  if (isPhrasingContent(childNode)) {
    return isParagraphElement(parentNode) || isListItemElement(parentNode) || isPhrasingContent(parentNode);
  }

  return true;
}

function createWrapperForIllegalParent(
  document: Document,
  parentNode: Node | null,
  childNode: Node,
): Element {
  if (isListItemElement(childNode)) {
    const preferredListType = childNode.getAttribute(EDITOR_DATA_LIST_TYPE);
    childNode.removeAttribute(EDITOR_DATA_LIST_TYPE);
    return document.createElement(preferredListType === 'ol' ? 'ol' : 'ul');
  }

  if (isListElement(parentNode)) {
    return document.createElement('li');
  }

  return document.createElement('div');
}

export function addFillerIfContainerEmpty(container: Node): void {
  const { ownerDocument: document } = container;
  if (!document) {
    return;
  }

  if (isEmptyBlockContainer(container)) {
    replaceAllChildren(container, document.createElement('br'));
  } else if (isEmptyPhrasingContainer(container)) {
    replaceAllChildren(container, document.createTextNode(NBSP));
  }
}

function isEmptyBlockContainer(container: Node): boolean {
  return (
    isElement(container) &&
    isBlockLevelContent(container) &&
    everyInIterable(
      container.childNodes,
      childNode => isText(childNode) && isOnlyCollapsibleWhitespace(childNode.data),
    )
  );
}

function isEmptyPhrasingContainer(container: Node): boolean {
  return (
    isElement(container) &&
    isPhrasingContent(container) &&
    everyInIterable(container.childNodes, childNode => isText(childNode) && childNode.data.length === 0)
  );
}

export function normalizeLists(container: Node): void {
  const document = container.ownerDocument;
  if (!document) {
    return;
  }

  // Iterate all list items in the container.
  const listItemIterator = document.createNodeIterator(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: candidate =>
      isListItemElement(candidate) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });
  let listItem;
  while ((listItem = listItemIterator.nextNode() as HTMLLIElement | null)) {
    // Find the only non-whitespace child node.
    const nonWhitespaceChildNodes = Array.from(
      takeFromIterable(
        filterIterable(listItem.childNodes, childNode => !isWhitespaceText(childNode)),
        2,
      ),
    );
    if (nonWhitespaceChildNodes.length !== 1) {
      continue;
    }

    // Check if the only child node is a list.
    const childList = nonWhitespaceChildNodes[0];
    if (!isListElement(childList)) {
      continue;
    }

    // Unwrap the child list from the list item, which avoids double bullets. (In the next step, it may be moved to the
    // previous list item, which would make it standard HTML.)
    unwrapNode(listItem);
  }

  // Iterate all lists in the container.
  const listIterator = document.createNodeIterator(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: candidate => (isListElement(candidate) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });
  let list;
  while ((list = listIterator.nextNode() as ListElement | null)) {
    // Check if this is a direct child of another list.
    if (!isListElement(list.parentNode)) {
      continue;
    }

    // Check if there is a previous list item.
    const previousNonWhitespaceSibling = findPreviousNonWhitespaceSibling(list);
    if (!isListItemElement(previousNonWhitespaceSibling)) {
      continue;
    }

    // Move the list to the end of the previous list item, consistent with HTML standards.
    previousNonWhitespaceSibling.appendChild(list);
  }
}
